/**
 * Size limits for SQL text that flows through the editor and the persisted
 * stores. Shared by the renderer and the Electron main process (file-open
 * guard), so it lives under `src/types` like the other cross-layer modules.
 *
 * Why these exist (crash class seen on 1.4.25, 2026-09-04/08): Monaco marks a
 * model above 256M characters as "too large for heap operation" and every
 * later `getValue()` throws `Operation would exceed heap memory limits`; the
 * session auto-save then tries to `JSON.stringify` the same body and hits
 * V8's maximum string length (`Invalid string length`). One tier lower, any
 * tab or history body past the ~10 MB localStorage quota throws
 * `QuotaExceededError` from the same persist path. All three were uncaught.
 *
 * The limits are deliberately far apart:
 *   - `EDITOR_TEXT_LIMIT_CHARS` gates what may ENTER the editor (file open,
 *     paste). 50M characters is well under Monaco's 256M heap threshold and
 *     above its 20 MB tokenization cut-off, so an accepted document is at
 *     worst un-highlighted, never crash-prone.
 *   - `PERSISTED_SQL_LIMIT_CHARS` gates what a persisted store keeps
 *     (session tab bodies, notebook cell sources, closed-tab history, query
 *     history). Bodies above it are omitted from persistence — localStorage
 *     holds ~10 MB for the whole origin, so persisting multi-megabyte scripts
 *     is not an option. The cap is PER BODY, not per session.
 */

const MEGABYTE = 1024 * 1024;

/** Editor text limit for user-facing copy; the char limit derives from it. */
export const EDITOR_TEXT_LIMIT_MB = 50;

/** Maximum SQL text (in characters) accepted into a query editor. */
export const EDITOR_TEXT_LIMIT_CHARS = EDITOR_TEXT_LIMIT_MB * MEGABYTE;

/** Persisted body cap for user-facing copy; the char cap derives from it. */
export const PERSISTED_SQL_LIMIT_MB = 1;

/** Maximum SQL body (in characters) a persisted store keeps for one entry. */
export const PERSISTED_SQL_LIMIT_CHARS = PERSISTED_SQL_LIMIT_MB * MEGABYTE;

/**
 * True when text of `chars` characters must not enter the editor.
 * A UTF-8 byte count is a safe stand-in for the character count (bytes >=
 * characters), which is how the main-process file guard uses it.
 */
export function exceedsEditorTextLimit(chars: number): boolean {
  return chars > EDITOR_TEXT_LIMIT_CHARS;
}

/** True when a SQL body must not be written to a persisted store. */
export function exceedsPersistedSqlLimit(sql: string | undefined | null): boolean {
  return typeof sql === 'string' && sql.length > PERSISTED_SQL_LIMIT_CHARS;
}

/** Whole megabytes, rounded up, for user-facing size copy ("480 MB"). */
export function formatWholeMegabytes(units: number): string {
  return `${Math.max(1, Math.ceil(units / MEGABYTE))} MB`;
}

/**
 * Low-cardinality size bucket for analytics properties. Never send the raw
 * size — it is high-cardinality and, for scripts, borderline user data.
 */
export function sizeBucketForChars(chars: number): string {
  if (chars <= 1 * MEGABYTE) return '<=1mb';
  if (chars <= 10 * MEGABYTE) return '1-10mb';
  if (chars <= 50 * MEGABYTE) return '10-50mb';
  if (chars <= 256 * MEGABYTE) return '50-256mb';
  return '>256mb';
}

export type OversizedTextSource = 'file' | 'paste' | 'drop';

const LIMIT_TAIL = `The editor accepts SQL up to ${EDITOR_TEXT_LIMIT_MB} MB — run larger scripts with your database's command-line tool.`;

/**
 * User-facing reason shown when text is refused. For a file, `chars` is the
 * file's size; for a paste or drop it is the size the tab WOULD reach, so the
 * message never contradicts itself when the tab is already near the limit.
 */
export function describeOversizedText(chars: number, what: OversizedTextSource): string {
  if (what === 'file') {
    return `This file is ${formatWholeMegabytes(chars)}. ${LIMIT_TAIL}`;
  }
  const verb = what === 'paste' ? 'Pasting' : 'Dropping';
  return `${verb} this text would bring the tab to ${formatWholeMegabytes(chars)}. ${LIMIT_TAIL}`;
}

/** Shown when a watched file grows past the limit on disk. */
export function describeOversizedExternalChange(fileName: string, bytes: number): string {
  return `${fileName} changed on disk and is now ${formatWholeMegabytes(bytes)}, over the ${EDITOR_TEXT_LIMIT_MB} MB editor limit. The tab keeps its current text.`;
}

/**
 * Replacement body for a persisted tab whose script was too large to keep.
 * A file-tied tab first tries to reload the script from disk on restore
 * (`pendingFileReload`); this placeholder is what the user sees when there
 * was no file, or the reload failed. It never carries the file binding, so
 * Save cannot write it over the real script.
 */
export function buildOmittedScriptPlaceholder(filePath?: string): string {
  const lines = [
    `-- This tab held a script larger than ${PERSISTED_SQL_LIMIT_MB} MB, which Jam SQL does not keep across restarts.`,
  ];
  if (filePath) {
    lines.push(`-- The file could not be reloaded. Reopen it from More > Open File: ${filePath}`);
  } else {
    lines.push('-- Save large scripts to a .sql file (Save As) so they can be reopened.');
  }
  return `${lines.join('\n')}\n`;
}

/**
 * Replacement source for a persisted NOTEBOOK CELL whose content was too large
 * to keep. Mirrors `buildOmittedScriptPlaceholder` one level down: the cap is
 * per cell, so only the oversized cells are replaced.
 *
 * A notebook has no reload-on-mount path (a file-tied query tab re-reads its
 * script via `pendingFileReload`; nothing does that for `.ipynb`), so a
 * notebook restored with an omitted cell is DETACHED from its file — Save must
 * never write this placeholder over the user's notebook. The path is named
 * here so the user can reopen it.
 */
export function buildOmittedCellPlaceholder(
  cellType: 'code' | 'javascript' | 'markdown',
  filePath?: string
): string {
  const prefix = cellType === 'markdown' ? '' : cellType === 'javascript' ? '// ' : '-- ';
  const lines = [
    `${prefix}This cell held content larger than ${PERSISTED_SQL_LIMIT_MB} MB, which Jam SQL does not keep across restarts.`,
  ];
  if (filePath) {
    lines.push(`${prefix}The notebook was detached from its file so it cannot be saved over. Reopen it from More > Open Notebook: ${filePath}`);
  } else {
    lines.push(`${prefix}Save large notebooks to an .ipynb file (Save As) so they can be reopened.`);
  }
  return `${lines.join('\n')}\n`;
}

/**
 * Banner text for a Table Designer tab restored without its buffers. Unlike a
 * script or a notebook cell, a Designer body must NEVER come back as a
 * placeholder: `currentSql` is what Save diffs against `initialSql` and then
 * executes as DDL, so a comment in that field would plan a migration that
 * drops every column. The buffers are omitted instead (the tab re-derives the
 * baseline), and this message explains the gap in the tab's error banner.
 */
export function buildOmittedDesignerDraftMessage(): string {
  return (
    `The unsaved table definition in this tab was larger than ${PERSISTED_SQL_LIMIT_MB} MB, so it was not kept ` +
    'across the restart. Nothing was applied to the database — reopen Design Table to start from the ' +
    "table's current definition."
  );
}

/**
 * Per-layer error for a Spatial Explorer query layer restored without its SQL.
 * Truncating the query is not an option — a shorter query is a DIFFERENT query
 * and would paint the wrong rows on the map — so the layer keeps its label and
 * fails visibly instead of running anything.
 */
export function buildOmittedLayerQueryMessage(): string {
  return (
    `This layer's query was larger than ${PERSISTED_SQL_LIMIT_MB} MB, so it was not kept across the restart. ` +
    'Remove the layer and add it again with the query.'
  );
}
